<template lang='pug'>
.tr.fp-tree-item(:class="className",ref="treeitem")
  fp-tree-cell(
    :key='index'
    :column-ratio='columnRatio',
    v-for='column, index in options.columns'
    :is-first='index === 0'
    :is-last='index === options.columns.length - 1'
    :index='index'
    :item='item'
    :column-item='column'
    :root='root'
    :options='options'
    :editable='typeof column.edit === "function"'
    :value='getValue(item, column, index)'
    @toggle="$emit('toggle')"
    @render="$emit('render')"
  )
  .td.fp-tree-cell.fp-tree-actions
    span.content
      fpui-action-list(color='blue', v-if='!allActionsLoading')
        i(
          :key='index'
          :class='getClassIcon(action, index)',
          v-tooltip='getTooltip(action)'
          @click='call(action, index, $event)',
          v-if='!action.if||action.if(item)',
          v-for='action, index in options.type[item.type].actions'
        )
      .actions-list-loading(v-else)
        i.fa.fa-spin.fa-spinner.disabled
</template>

<style lang="less">
  @import "~@/shared/styles/_variables.less";
  tr.fp-tree-item {
    position: relative;
    z-index:1;
  }
  tr.fp-tree-item.drop-target {
    background:#ebf9fe;
  }
</style>

<script>
import FpTreeCell from './FpTreeCell'
import get from 'lodash/get'
import interact from 'interactjs'

export default {
  components: {
    FpTreeCell
  },
  props: {
    columnRatio: { type: Number },
    isLast: { type: Boolean },
    item: { type: Object },
    root: { type: Object },
    treeId: { type: String },
    options: { type: Object }
  },
  data () {
    return {
      dragclass: null,
      actionsLoading: this.options.type[this.item.type].actions.map(() => false)
    }
  },
  mounted () {
    let interactElem = interact(this.$refs.treeitem)
    if (this.options.type[this.item.type].draggable) {
      interactElem.draggable({
        inertia: true,
        autoScroll: true,
        onstart: (event) => {
          event.target.item = this.item
          this.dragclass = 'dragging'
        },
        onmove: (event) => {
          // keep the dragged position in the data-x/data-y attributes
          let x = (parseFloat(event.target.getAttribute('data-x')) || 0) + event.dx
          let y = (parseFloat(event.target.getAttribute('data-y')) || 0) + event.dy

          // translate the element
          event.target.style.webkitTransform = 'translate(' + x + 'px, ' + y + 'px)'
          event.target.style.transform = event.target.style.webkitTransform
          event.target.style.zIndex = 20000

          // update the posiion attributes
          event.target.setAttribute('data-x', x)
          event.target.setAttribute('data-y', y)
        },
        onend: (event) => {
          event.target.style.webkitTransform = 'translate(0px, 0px)'
          event.target.style.transform = event.target.style.webkitTransform
          event.target.setAttribute('data-x', 0)
          event.target.setAttribute('data-y', 0)
          event.target.style.zIndex = 1
          this.dragclass = null
        }
      })
    }
    interactElem.dropzone({
      ondragenter: (event) => {
        let typeOptions = this.options.type[this.item.type]
        if (typeOptions.droppable) {
          return event.target.classList.add('drop-target')
        }
        if (this.item.parent && this.item.parent.type === 'root') {
          if (this.root.children.indexOf(event.relatedTarget.item) !== -1) return
          event.target.parentNode.classList.add('drop-target')
        }
      },
      ondragleave: (event) => {
        let typeOptions = this.options.type[this.item.type]
        if (typeOptions.droppable) {
          return event.target.classList.remove('drop-target')
        }
        if (this.item.parent && this.item.parent.type === 'root') {
          event.target.parentNode.classList.remove('drop-target')
        }
      },
      ondrop: async (event) => {
        if (!event.relatedTarget.item) return false
        if (this.isChildOf(event.relatedTarget.item)) return false
        let typeOptions = this.options.type[this.item.type]
        if (!typeOptions.droppable && !(this.item.parent && this.item.parent.type === 'root')) {
          return
        }
        if (typeOptions.droppable) {
          event.target.classList.remove('drop-target')
          let idx = event.relatedTarget.item.parent.children.indexOf(event.relatedTarget.item)
          if (idx !== -1) event.relatedTarget.item.parent.children.splice(idx, 1)
          if (typeof this.item.children === 'function') {
            this.item.children = await this.item.children()
          }
          event.relatedTarget.item = {...event.relatedTarget.item, isLast: true}
          this.item.children.push(event.relatedTarget.item)
          event.relatedTarget.item.parent = this.item
          // adding loader in treeview
          this.$emit('loading', true)
          this.$emit('render')
          await Promise.resolve(typeOptions.onDrop(event.relatedTarget.item, this.item, this.root))
          this.$emit('loading', false)
          this.$emit('render')
          event.stopPropagation()
        } else {
          event.target.parentNode.classList.remove('drop-target')
          if (this.root.children.indexOf(event.relatedTarget.item) !== -1) return
          let idx = event.relatedTarget.item.parent.children.indexOf(event.relatedTarget.item)
          if (idx !== -1) event.relatedTarget.item.parent.children.splice(idx, 1)
          event.relatedTarget.item = {...event.relatedTarget.item, isLast: false}
          this.root.children.push(event.relatedTarget.item)
          event.relatedTarget.item.level = 0
          event.relatedTarget.item.parent = this.item
          // adding loader in treeview
          this.$emit('loading', true)
          await Promise.resolve(this.options.onDrop(event.relatedTarget.item, this.item, this.root))
          this.$emit('loading', false)
          this.$emit('render')
          event.stopPropagation()
        }
      }
    })
  },
  computed: {
    allActionsLoading () { return !this.actionsLoading.some(loading => loading === false) },
    className () {
      let className = [`tree-item_${this.treeId}`]
      if (this.isLast) className.push('last')
      if (get(this.options, `type.${this.item.type}.draggable`)) className.push('sortable-item')
      if (this.dragclass) className.push(this.dragclass)
      return className.join(' ')
    },
    itemActions () {
      let that = this
      return {
        add (newItem) {
          if (!Array.isArray(that.item.children)) {
            throw new Error('AddChildrenError Children are loaded async, we cannot put a child here')
          }
          that.item.children.push(newItem)
          that.$emit('render')
        },
        addToParent (newItem) {
          that.item.parent.children.push(newItem)
          that.$emit('render')
        },
        remove () {
          const idx = that.item.parent.children.indexOf(that.item)
          if (idx !== -1) {
            that.item.parent.children.splice(idx, 1)
            that.$emit('render')
          }
        },
        update (item) {
          Object.assign(that.item, item)
          that.$emit('render')
        },
        emit (event) { that.$emit(event) }
      }
    }
  },
  methods: {
    getValue (item, column, index) {
      let typeColumn = get(this.options, `type.${item.type}.columns[${index}]`)
      let tmpCol = typeColumn || column


      if (typeof tmpCol.function === 'function') {
        return tmpCol.function(item)
      }
      return get(item, tmpCol.target) || '-'
    },
    getTooltip (action) {
      if (typeof action.tooltip === 'function') {
        return action.tooltip(this.item)
      }
      return action.tooltip
    },
    actionDisabled (action) {
      if (typeof action.disabled !== 'undefined') {
        if (typeof action.disabled === 'function') return action.disabled(this.item)
        return action.disabled
      }
      return false
    },
    getClassIcon (action, index) {
      let className = []
      if (this.actionsLoading[index]) {
        className.push('fa fa-spin fa-spinner disabled')
      } else {
        className.push(this.getIcon(action.icon))
      }
      if (this.actionDisabled(action)) className.push('disabled')
      return className.join(' ')
    },
    getIcon (icon) {
      if (typeof icon === 'function') icon(this.item)
      return icon
    },
    isChildOf (item) {
      let parent = this.item.parent
      while (parent) {
        if (parent === item) {
          return true
        }
        parent = parent.parent
      }
      return false
    },
    call (action, index, event) {
      if (this.actionDisabled(action)) return
      const loading = val => {
        if (action.allLoadOnClick) {
          this.actionsLoading.forEach((action, idx) => this.$set(this.actionsLoading, idx, val))
        } else if (action.loadOnClick) {
          this.actionsLoading.splice(index, 1, val)
        }
      }
      loading(true)
      Promise.resolve(action.click.call(this.itemActions, this.item, this.root, action, event))
        .then(() => loading(false))
        .catch(() => loading(false))
    }
  }
}
</script>
